/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { useCallback, useState, useMemo } from 'react';
import MikeVisualizerLib from '../../MikeVisualizer/lib/MikeVisualizer';
import Down from '../../icons/Down.svg?react';
import Up from '../../icons/Up.svg?react';
import Typography from '@mui/material/Typography';
import EyeVisible from '../../icons/EyeVisible.svg?react';
import EyeHidden from '../../icons/EyeHidden.svg?react';
import { useDispatch, useSelector } from 'react-redux';
import { legendGroupHideAll, legendGroupShowAll } from '../../actions/legend';
import { ILayerGroup, ILegendLayer, MODEL_RUN_GROUP_ID } from '../../reducers/legend';
import { IElementDataArray, IRenderElement, IThreeDRenderElement } from '../../MikeVisualizer/lib/IMikeVisualizerModels';
import { Layer } from './Layer';
import { IGetProject } from '../../model/IGetProject';
import { IState } from '../../reducers';
import { useIntl } from 'react-intl';
import { uploadBackground } from '../../actions/mapContent';
import UploadButton from '../UploadButton/UploadButton';
import { BACKGROUND, MMG_PREFIX } from '../../shared/constants';
import mikeSharedTheme, { mikePalette } from '../../styles/mikeSharedTheme';
// import { iconPrimaryStyle } from '../../styles/iconStyles';
import { StyledSelect } from '../Sidebar/StyledSelect';
import { selectStyle } from '../../styles/sharedDropdownStyles';

const iconPrimaryStyle = (disabled = false) => {
  const color = disabled ? mikePalette.primary.light : mikePalette.primary.main
  return css`
    line {      
      stroke: ${color};
    }
    path {
      fill: ${color};
      stroke: ${color};
    }
  `
}

const TitleContainerStyle = (visible: boolean) => {
  const backgroundColor = visible ? mikePalette.mediumGrey.main : mikePalette.mediumGrey.light;  
  return css`
  display: flex;
  justify-content: space-between;
  align-items: center;  
  background-color: ${backgroundColor};
  border-top: 1px solid ${mikePalette.mediumGrey.dark};
`;
}

const TitleAndButtonStyle = css`
  display: flex;
  align-items: center;
`;

export const TitleStyle = (visible: boolean) => {  
  const color = visible ? mikePalette.primary.main : mikePalette.primary.light;
  return css`
  padding-left: ${mikeSharedTheme.spacing(1)};
  &.MuiTypography-root {
    color: ${color};
  }  
`;
}

const legendSelectContainerStyle = css`
  display: flex; 
  align-items: center;
  padding: ${mikeSharedTheme.spacing(0.5)};
`;

const titleContainerStyle = css`
  padding-right: ${mikeSharedTheme.spacing(0.5)};
`;

interface IProps {
  layerGroup: ILayerGroup; 
}

const VALUEFIELD = "id";

export const LayerGroup = (props: IProps) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const {layerGroup} = props;

  const project: IGetProject | null = useSelector(
    (state: IState) => state.projectContent.project
  );

  const { getState } = MikeVisualizerLib;
  const { renderedElements, rendered2DElements } = getState();

  const isModelRunLayer = useMemo(() => {
    return layerGroup && layerGroup.id === MODEL_RUN_GROUP_ID
  },[layerGroup])

  const canUpdate = useMemo(() => {    
    return project && project.capabilities && project.capabilities.canUpdateContent
  }, [project])

  const layersToBeLoadedByUser = useMemo(() => {
    const layers = layerGroup.layer;
    return layers.filter((l: ILegendLayer) => l.canBeLoadedByUser)
  },[layerGroup])

  const loadingLayers = useMemo(() => {
    const layers = layerGroup.layer;
    return layers.filter((l: ILegendLayer) => l.loading)
  }, [layerGroup])

  const loadedLayers = useMemo(() => {
    const layers = layerGroup.layer;
  
    const two2DLayerIds = rendered2DElements.map((re: IRenderElement) => re.id)
    const two3DLayerIds = renderedElements.map((re: IThreeDRenderElement) => re.id)
   
    const loadedLayers = layers.filter((l: ILegendLayer) => 
      l.isTwoD ? two2DLayerIds.includes(l.id) : two3DLayerIds.includes(l.id))
    const sortedLayers = loadedLayers.sort((a: ILegendLayer, b: ILegendLayer) => {return b.order - a.order})
    return sortedLayers
  }, [layerGroup, renderedElements, rendered2DElements])

  const allLayers =  useMemo(() => {
    const all = Array<ILegendLayer>()
    return all.concat(loadedLayers).concat(loadingLayers).concat(layersToBeLoadedByUser);
  }, [layersToBeLoadedByUser, loadedLayers, loadingLayers])

  const allLayersHidden = useMemo(() => {
    const hiddenLayers = allLayers.filter((l: ILegendLayer) => l.visible === false); 
    return hiddenLayers.length === allLayers.length
  }, [allLayers])

  const [changedGradient, setChangedGradient] = useState("")
  const dataArrays = useMemo(() => {
    if (layerGroup.id && layerGroup.id === MODEL_RUN_GROUP_ID && layerGroup.layer && layerGroup.layer.length > 0){  
      const layer = layerGroup.layer[0]
      const renderedElement: IThreeDRenderElement = renderedElements.find(({ id }) => id === layer.id);
      if (renderedElement){  
        setChangedGradient(renderedElement.gradientAttributeName)
        return renderedElement.dataArrays.filter(((d: IElementDataArray) => !d.id.startsWith(MMG_PREFIX)));
      }
    }
    return [];
  }, [layerGroup.id, layerGroup.layer, renderedElements])
  
 
  const [layerGroupOpen, setLayerGroupOpen] = useState(isModelRunLayer);

  const handleToggleVisibiltyAllLayers = useCallback(() => {
    const showAllLayers = allLayersHidden
    if (showAllLayers){
      dispatch(legendGroupShowAll(layerGroup))
    }
    else{
      dispatch(legendGroupHideAll(layerGroup))
    }
  }, [layerGroup, allLayersHidden, dispatch])

  const onUploadFromComputer = (files) => {     
    if (files.length > 0){       
      const file = files[0] 
      dispatch(uploadBackground(file, file.name, BACKGROUND))     
    }
  };
 
  const handleChangeProperty = useCallback((property: string) => {
    if (layerGroup && layerGroup.layer && layerGroup.layer.length > 0){
      const layer = layerGroup.layer[0]
      const { updateElementsGradient } = MikeVisualizerLib;      
      updateElementsGradient([layer.id], property, {gradientPreset:"erdc_rainbow_bright", numberOfLegends: 10, numberOfSignificantDigits:3}); 
      setChangedGradient(property)
    }        
  },[layerGroup])

  return (     
    <div>
      <div css={TitleContainerStyle(!allLayersHidden)} key="layergroup">
        <div css={TitleAndButtonStyle}>
          {layerGroupOpen ?  <Up css={iconPrimaryStyle(allLayersHidden)}  onClick={() => setLayerGroupOpen(!layerGroupOpen)}/>: 
          <Down css={iconPrimaryStyle(allLayersHidden)} onClick={() => setLayerGroupOpen(!layerGroupOpen)} />}
           {layerGroup.id === MODEL_RUN_GROUP_ID && dataArrays && dataArrays.length > 1 ?
            <div css={legendSelectContainerStyle}>
              <div css={titleContainerStyle}>
                <Typography css= {TitleStyle(!allLayersHidden)}  variant="body2"><b>{layerGroup.title}</b></Typography>
                {layerGroup.subTitle && <Typography css= {TitleStyle(!allLayersHidden)}  variant="body2"><b>{layerGroup.subTitle}</b></Typography>}
              </div>
              <StyledSelect 
                name={changedGradient}
                value={changedGradient}         
                valueField={VALUEFIELD}
                primaryField={VALUEFIELD}
                items={dataArrays}
                onChange={(event: React.ChangeEvent<any>) => handleChangeProperty(event.target.value)}         
                showColorIcon={false}
                addErrorPlaceHolder={false}
                selectSX={selectStyle}
              />
            </div> : 
            <Typography css= {TitleStyle(!allLayersHidden)}  variant="body2"><b>{layerGroup.title}</b></Typography>
          }          
        </div>
        {layerGroup && layerGroup.uploadType && <UploadButton 
          disabled={!canUpdate} 
          fileType={'.zip'} 
          label={intl.formatMessage({id:'desktop.upload'})} 
          onDrop={onUploadFromComputer}
          info={[intl.formatMessage({id: 'autoMesh.uploadShapefile_hint'})]}
        />}
        {allLayersHidden ? <EyeHidden  css={iconPrimaryStyle(true)} onClick={handleToggleVisibiltyAllLayers}/> : <EyeVisible onClick={handleToggleVisibiltyAllLayers}/>}
      </div>
      {layerGroupOpen && allLayers.map((l: ILegendLayer, index) => <Layer attributeName={changedGradient} key={index} layer={l} layerGroup={layerGroup}/>)}
    </div>
  );
};

export default LayerGroup